import React from 'react';
import { formatCurrency } from '../utils/format';

interface ClientData {
  clientName: string;
  businessName: string;
  sessionDate: string;
  adviserName: string;
  clientAge: number;
  partnerAge: number;
  salary: number;
  dividends: number;
  otherIncome: number;
  payeIncome: number;
  rentalIncomeNet: number;
  savingsInterest: number;
  otherDividends: number;
  expensesReimbursement: number;
  taxPaymentMethod: 'monthly' | 'selfAssessment';
  monthlyHMRC: number;
  mortgagePayment: number;
  mortgageOP: number;
  livingCosts: number;
  isaContribution: number;
  efTopUp: number;
  pensionMonthly: number;
  pensionCompanyFunded: number;
  sinkingFundsTotal: number;
  efAccount1Name: string;
  efAccount1Balance: number;
  efAccount1Rate: number;
  efAccount1Monthly: number;
  efAccount2Name: string;
  efAccount2Balance: number;
  efAccount2Rate: number;
  efAccount2Monthly: number;
  pensionBalance: number;
  isaBalance: number;
}

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

const fmt = formatCurrency;

const fmtPct = (n: number) => `${n.toFixed(1)}%`;

export default function FifteenPercentCheck({ data }: Props) {
  // Gross annual income (salary + dividends are annual in ClientData)
  const otherIncome = (data.payeIncome || 0) + (data.rentalIncomeNet || 0) + (data.savingsInterest || 0) + (data.otherDividends || 0);
  const grossAnnual = data.salary + data.dividends + otherIncome;
  const grossMonthly = grossAnnual / 12;

  const target = grossMonthly * 0.15;

  // What's currently going in (monthly)
  const pensionPersonal  = data.pensionMonthly;
  const pensionEmployer  = data.pensionCompanyFunded;
  const isaNow           = data.isaContribution;
  const total            = pensionPersonal + pensionEmployer + isaNow;

  const gap        = target - total;
  const onTrack    = total >= target;
  const pctActual  = grossMonthly > 0 ? (total / grossMonthly) * 100 : 0;
  const barWidth   = Math.min(100, (total / Math.max(target, 1)) * 100);

  // Status colour helpers
  const statusColour = onTrack ? 'green' : pctActual >= 10 ? 'amber' : 'red';
  const statusLabel  = onTrack ? 'On track ✓' : pctActual >= 10 ? 'Getting there' : 'Action needed';
  const statusBg     = { green: 'bg-green-50', amber: 'bg-amber-50', red: 'bg-red-50' }[statusColour];
  const statusText   = { green: 'text-green-700', amber: 'text-amber-700', red: 'text-red-700' }[statusColour];
  const barColour    = { green: 'bg-green-500', amber: 'bg-amber-400', red: 'bg-red-400' }[statusColour];
  const borderColour = { green: 'border-green-200', amber: 'border-amber-200', red: 'border-red-200' }[statusColour];

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-ma-navy">15% Retirement Check</h2>
        <p className="text-gray-500 mt-1 text-sm">
          Ramsey's rule: invest 15% of gross income into retirement. For UK directors, that means pension + ISA.
        </p>
      </div>

      {/* Status badge */}
      <div className={`rounded-2xl border ${borderColour} ${statusBg} px-6 py-5 mb-6 flex items-center justify-between`}>
        <div>
          <div className={`text-xs font-semibold uppercase tracking-wide ${statusText} mb-1`}>{statusLabel}</div>
          <div className={`text-3xl font-bold ${statusText}`}>{fmtPct(pctActual)}</div>
          <div className={`text-sm ${statusText} opacity-70 mt-0.5`}>of gross income going to retirement</div>
        </div>
        <div className="text-right">
          <div className="text-gray-400 text-xs mb-1">Target</div>
          <div className="text-2xl font-bold text-gray-600">15.0%</div>
          <div className="text-gray-400 text-xs">{fmt(target)}/month</div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-6">
        <div className="flex justify-between text-xs text-gray-400 mb-1.5">
          <span>0%</span>
          <span className="font-medium text-gray-600">{fmtPct(pctActual)} actual</span>
          <span>15% target</span>
        </div>
        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-700 ${barColour}`}
            style={{ width: `${barWidth}%` }}
          />
        </div>
        {/* 15% marker */}
        <div className="relative h-0">
          <div className="absolute top-0 left-full -translate-x-px -translate-y-3 w-px h-3 bg-gray-400 opacity-50" />
        </div>
      </div>

      {/* Breakdown */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden mb-4">
        <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Gross income basis</span>
        </div>

        <div className="divide-y divide-gray-50">
          <Row label="Gross annual income" value={fmt(grossAnnual)} sub="salary + dividends" />
          <Row label="15% monthly target" value={fmt(target)} sub="of gross monthly" highlight />
        </div>

        <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 mt-1">
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Current contributions</span>
        </div>

        <div className="divide-y divide-gray-50">
          <Row
            label="Pension — personal"
            value={fmt(pensionPersonal)}
            sub="/month"
            warn={pensionPersonal === 0}
          />
          <Row
            label="Pension — employer"
            value={fmt(pensionEmployer)}
            sub="saves 25% CT on full amount"
            good={pensionEmployer > 0}
            warn={pensionEmployer === 0}
          />
          <Row
            label="Stocks & Shares ISA"
            value={fmt(isaNow)}
            sub="/month · tax-free"
            warn={isaNow === 0}
          />
          <div className="flex items-center justify-between px-5 py-3.5 bg-gray-50">
            <span className="text-sm font-bold text-ma-navy">Total monthly</span>
            <div className="text-right">
              <span className={`text-lg font-bold ${onTrack ? 'text-green-600' : 'text-gray-700'}`}>{fmt(total)}</span>
              <div className="text-xs text-gray-400">{fmtPct(pctActual)} of gross</div>
            </div>
          </div>
        </div>
      </div>

      {/* Gap or surplus */}
      {!onTrack && gap > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-1">Monthly shortfall</div>
              <div className="text-2xl font-bold text-amber-700">{fmt(gap)}/month</div>
              <div className="text-xs text-amber-500 mt-0.5">to reach the 15% target</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-amber-500 mb-1">Annual shortfall</div>
              <div className="text-lg font-semibold text-amber-700">{fmt(gap * 12)}</div>
            </div>
          </div>

          <div className="border-t border-amber-200 pt-3">
            <div className="text-xs text-amber-600 font-medium mb-2">How to close the gap:</div>
            <div className="space-y-1.5">
              <div className="text-xs text-amber-700 flex items-start gap-2">
                <span className="mt-0.5 shrink-0">•</span>
                <span>
                  <strong>Employer pension (recommended first):</strong> the company claims CT relief on the full contribution — costs {fmt(Math.round(gap * 0.75))} net of tax to put {fmt(gap)} in.
                </span>
              </div>
              <div className="text-xs text-amber-700 flex items-start gap-2">
                <span className="mt-0.5 shrink-0">•</span>
                <span>
                  <strong>ISA top-up:</strong> tax-free and fully flexible — no access restrictions before 55.
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {onTrack && (
        <div className="bg-green-50 border border-green-200 rounded-2xl px-5 py-4 mb-6">
          <div className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-1">Ahead of target</div>
          <div className="text-2xl font-bold text-green-700">{fmt(Math.abs(gap))}<span className="text-sm font-normal text-green-500">/month surplus</span></div>
          <div className="text-xs text-green-600 mt-2">
            Investing {fmtPct(pctActual)} — {fmtPct(pctActual - 15)} above the 15% benchmark. This excess could accelerate mortgage overpayments or build additional ISA reserves.
          </div>
        </div>
      )}

      {/* Summary strip */}
      <div className="bg-ma-navy rounded-2xl p-5 text-white">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-white/60 text-xs mb-1">Gross monthly</div>
            <div className="text-base font-bold">{fmt(grossMonthly)}</div>
          </div>
          <div>
            <div className="text-white/60 text-xs mb-1">15% target</div>
            <div className="text-base font-bold">{fmt(target)}</div>
          </div>
          <div>
            <div className="text-white/60 text-xs mb-1">Currently investing</div>
            <div className={`text-base font-bold ${onTrack ? 'text-green-300' : 'text-amber-300'}`}>{fmt(total)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper row component
function Row({
  label, value, sub, highlight, good, warn,
}: {
  label: string;
  value: string;
  sub?: string;
  highlight?: boolean;
  good?: boolean;
  warn?: boolean;
}) {
  return (
    <div className={`flex items-center justify-between px-5 py-3.5 ${highlight ? 'bg-blue-50/40' : ''}`}>
      <div>
        <div className={`text-sm ${highlight ? 'font-semibold text-ma-navy' : 'text-gray-700'}`}>{label}</div>
        {sub && (
          <div className={`text-xs mt-0.5 ${good ? 'text-green-500' : warn ? 'text-amber-400' : 'text-gray-400'}`}>
            {warn && '⚠ '}
            {sub}
          </div>
        )}
      </div>
      <div className={`text-sm font-semibold ${highlight ? 'text-blue-700' : good ? 'text-green-600' : 'text-gray-700'}`}>
        {value}
      </div>
    </div>
  );
}
